import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';
import './RealizedGains.css';

function DividendForm({ dividend = null, onSuccess, onCancel }) {
  const isEdit = dividend !== null;

  const [formData, setFormData] = useState({
    statementType: dividend?.statementType || 'INDIVIDUAL',
    assetName: dividend?.assetName || '',
    ticker: dividend?.ticker || '',
    assetType: dividend?.assetType || 'Stock',
    broker: dividend?.broker || '',
    dividendPerShare: dividend?.dividendPerShare || '',
    sharesOwned: dividend?.sharesOwned || '',
    totalAmount: dividend?.totalDividendAmount || '',
    paymentDate: dividend?.date || new Date().toISOString().split('T')[0],
    exDividendDate: dividend?.exDividendDate || '',
    taxWithheld: dividend?.taxWithheld || '',
    currency: dividend?.currency || 'USD',
    linkedHoldingId: dividend?.linkedHoldingId || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ASSET_TYPES = ['Stock', 'ETF', 'REIT', 'Other'];
  const CURRENCIES = ['USD', 'EUR', 'GBP', 'SEK', 'NOK', 'DKK'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const isAnnual = formData.statementType === 'ANNUAL';

      // Validation based on statement type
      if (isAnnual) {
        // Annual statement validation
        if (!formData.broker || !formData.totalAmount || !formData.paymentDate) {
          throw new Error('Please fill in all required fields: Broker, Total Amount, Payment Date');
        }

        if (isNaN(parseFloat(formData.totalAmount)) || parseFloat(formData.totalAmount) < 0) {
          throw new Error('Total amount must be a non-negative number');
        }
      } else {
        // Individual dividend validation
        if (!formData.assetName || !formData.ticker || !formData.dividendPerShare ||
            !formData.sharesOwned || !formData.paymentDate) {
          throw new Error('Please fill in all required fields');
        }

        // Validate numeric values
        if (isNaN(parseFloat(formData.dividendPerShare)) || parseFloat(formData.dividendPerShare) < 0) {
          throw new Error('Dividend per share must be a non-negative number');
        }

        if (isNaN(parseFloat(formData.sharesOwned)) || parseFloat(formData.sharesOwned) <= 0) {
          throw new Error('Shares owned must be a positive number');
        }
      }

      // Common validation
      if (formData.taxWithheld && (isNaN(parseFloat(formData.taxWithheld)) || parseFloat(formData.taxWithheld) < 0)) {
        throw new Error('Tax withheld must be a non-negative number');
      }

      // Build payload based on statement type
      const payload = {
        statementType: formData.statementType,
        currency: formData.currency,
        paymentDate: formData.paymentDate,
        taxWithheld: formData.taxWithheld ? parseFloat(formData.taxWithheld) : 0,
        exDividendDate: formData.exDividendDate || null,
        linkedHoldingId: formData.linkedHoldingId || null,
      };

      if (isAnnual) {
        payload.broker = formData.broker;
        payload.totalAmount = parseFloat(formData.totalAmount);
      } else {
        payload.assetName = formData.assetName;
        payload.ticker = formData.ticker;
        payload.assetType = formData.assetType;
        payload.dividendPerShare = parseFloat(formData.dividendPerShare);
        payload.sharesOwned = parseFloat(formData.sharesOwned);
      }

      if (isEdit) {
        await axios.put(
          `${API_BASE_URL}/transactions/dividends/${dividend.transactionId}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/transactions/dividends`,
          payload,
          { withCredentials: true }
        );
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving dividend:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save dividend');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total dividend amount preview (for individual dividends)
  const totalDividendAmount = formData.dividendPerShare && formData.sharesOwned
    ? (parseFloat(formData.dividendPerShare) * parseFloat(formData.sharesOwned)).toFixed(2)
    : 0;

  const isAnnual = formData.statementType === 'ANNUAL';

  return (
    <div className="modal-overlay">
      <div className="modal-content dividend-form">
        <div className="modal-header">
          <h2>{isEdit ? 'Edit' : 'Add'} Dividend</h2>
          <button onClick={onCancel} className="btn-close" disabled={loading}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          {/* Statement Type Selector */}
          <div className="statement-type-selector">
            <label>
              <input
                type="radio"
                name="statementType"
                value="INDIVIDUAL"
                checked={formData.statementType === 'INDIVIDUAL'}
                onChange={handleChange}
                disabled={loading}
              />
              Individual Dividend (per ticker)
            </label>
            <label>
              <input
                type="radio"
                name="statementType"
                value="ANNUAL"
                checked={formData.statementType === 'ANNUAL'}
                onChange={handleChange}
                disabled={loading}
              />
              Annual Statement (total from broker)
            </label>
          </div>

          <div className="form-grid">
            {/* Show broker field for annual statements */}
            {isAnnual && (
              <div className="form-row">
                <label htmlFor="broker">Broker/Source *</label>
                <input
                  type="text"
                  id="broker"
                  name="broker"
                  value={formData.broker}
                  onChange={handleChange}
                  placeholder="e.g., Interactive Brokers, Trading 212"
                  required
                  disabled={loading}
                />
              </div>
            )}

            {/* Show asset fields for individual dividends */}
            {!isAnnual && (
              <>
                <div className="form-row">
                  <label htmlFor="assetName">Asset Name *</label>
                  <input
                    type="text"
                    id="assetName"
                    name="assetName"
                    value={formData.assetName}
                    onChange={handleChange}
                    placeholder="e.g., Microsoft Corporation"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor="ticker">Ticker *</label>
                  <input
                    type="text"
                    id="ticker"
                    name="ticker"
                    value={formData.ticker}
                    onChange={handleChange}
                    placeholder="e.g., MSFT"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor="assetType">Asset Type *</label>
                  <select
                    id="assetType"
                    name="assetType"
                    value={formData.assetType}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    {ASSET_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <label htmlFor="dividendPerShare">Dividend per Share *</label>
                  <input
                    type="number"
                    id="dividendPerShare"
                    name="dividendPerShare"
                    value={formData.dividendPerShare}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="e.g., 0.75"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor="sharesOwned">Shares Owned *</label>
                  <input
                    type="number"
                    id="sharesOwned"
                    name="sharesOwned"
                    value={formData.sharesOwned}
                    onChange={handleChange}
                    step="0.01"
                    min="0.01"
                    placeholder="e.g., 200"
                    required
                    disabled={loading}
                  />
                </div>
              </>
            )}

            {/* Show total amount for annual statements */}
            {isAnnual && (
              <div className="form-row">
                <label htmlFor="totalAmount">Total Dividend Amount *</label>
                <input
                  type="number"
                  id="totalAmount"
                  name="totalAmount"
                  value={formData.totalAmount}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="e.g., 1250.00"
                  required
                  disabled={loading}
                />
              </div>
            )}

            <div className="form-row">
              <label htmlFor="currency">Currency *</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {CURRENCIES.map(curr => (
                  <option key={curr} value={curr}>{curr}</option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label htmlFor="paymentDate">Payment Date *</label>
              <input
                type="date"
                id="paymentDate"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                required
                disabled={loading}
              />
            </div>

            <div className="form-row">
              <label htmlFor="exDividendDate">Ex-Dividend Date</label>
              <input
                type="date"
                id="exDividendDate"
                name="exDividendDate"
                value={formData.exDividendDate}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="form-row">
              <label htmlFor="taxWithheld">Tax Withheld</label>
              <input
                type="number"
                id="taxWithheld"
                name="taxWithheld"
                value={formData.taxWithheld}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="e.g., 22.50"
                disabled={loading}
              />
            </div>
          </div>

          {/* Preview section */}
          {!isAnnual && totalDividendAmount > 0 && (
            <div className="dividend-preview">
              <strong>Total Dividend Amount:</strong> {formData.currency} {totalDividendAmount}
            </div>
          )}
          {isAnnual && formData.totalAmount > 0 && (
            <div className="dividend-preview">
              <strong>Annual Statement Total:</strong> {formData.currency} {parseFloat(formData.totalAmount).toFixed(2)}
            </div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Saving...' : (isEdit ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DividendForm;
